'use client'

import { motion, Transition } from 'framer-motion'
import { useEffect, useRef, VideoHTMLAttributes } from 'react'
import { IMAGE_PATH } from '../../config/constants'
import useToggle from '@/hooks/use-toggle'
import { cn } from '@/util/cn'

const sharedVideoProps: VideoHTMLAttributes<HTMLVideoElement> = {
    src: `${IMAGE_PATH}/coaster.mp4`,
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
}

const LAYOUT_ID = 'coaster-video'

const layoutTransition: Transition = {
    duration: 0.15,
    ease: 'easeInOut',
    layout: { duration: 0.15, ease: 'easeInOut' },
}

type Props = {
    classNames?: Partial<Record<'videoWrapper' | 'overlayWrapper', string>>
}

const Video = ({ classNames }: Props) => {
    const inlineVideoRef = useRef<HTMLVideoElement>(null)
    const [isFullscreen, expand, collapse] = useToggle(false)

    useEffect(() => {
        if (!isFullscreen) return

        const handleScroll = () => {
            collapse()
        }

        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [collapse, isFullscreen])

    const handleVideoRef = (el: HTMLVideoElement) => {
        if (el && inlineVideoRef.current) {
            el.currentTime = inlineVideoRef.current.currentTime
        }
    }

    return (
        <div>
            <motion.div
                layoutId={LAYOUT_ID}
                whileHover={{ scale: 1.1 }}
                className={cn(
                    'relative mx-auto aspect-[16/9] translate-y-[8.25rem] cursor-pointer overflow-hidden',
                    classNames?.videoWrapper
                )}
                onClick={expand}
                transition={layoutTransition}
            >
                <video
                    ref={inlineVideoRef}
                    className="h-full w-full cursor-pointer object-cover"
                    {...sharedVideoProps}
                />
            </motion.div>

            {isFullscreen && (
                <motion.div
                    layoutId={LAYOUT_ID}
                    className={cn(
                        'fixed inset-0 z-100 m-auto flex size-full cursor-pointer items-center justify-center bg-black',
                        classNames?.overlayWrapper
                    )}
                    onClick={collapse}
                    transition={layoutTransition}
                >
                    <video
                        className="size-full object-cover object-center"
                        ref={handleVideoRef}
                        {...sharedVideoProps}
                    />
                </motion.div>
            )}
        </div>
    )
}

export default Video
